import { useState, useEffect } from "react";
import { activate } from "../../../utils/keyboard-activate";
import type { XPostResponse } from "../../../types/crm";
import { getXPostsForProfile } from "../../../services/crmXPosts";
import "./XPostsSection.css";

interface XPostsSectionProps {
  profileId: string;
  refreshKey?: number;
}

export const XPostsSection = ({ profileId, refreshKey }: XPostsSectionProps) => {
  const [posts, setPosts] = useState<XPostResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getXPostsForProfile(profileId)
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [profileId, refreshKey]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const statusClass = (status: string) => {
    switch (status) {
      case "Posted": return "xposts-section__status--posted";
      case "Failed": return "xposts-section__status--failed";
      case "Pending":
      case "Uploading":
      case "Processing": return "xposts-section__status--pending";
      default: return "";
    }
  };

  return (
    <div className="xposts-section">
      <div
        className="xposts-section__header"
        {...activate(() => setCollapsed(!collapsed))}
      >
        <span className="xposts-section__header-title">
          X Posts
          {posts.length > 0 && (
            <span className="xposts-section__count">{posts.length}</span>
          )}
        </span>
        <span className="xposts-section__toggle">{collapsed ? "+" : "−"}</span>
      </div>

      {!collapsed && (
        <div className="xposts-section__body">
          {loading ? (
            <div className="xposts-section__empty">Loading...</div>
          ) : posts.length === 0 ? (
            <div className="xposts-section__empty">
              No X posts yet. Use "Post to X" to promote this player.
            </div>
          ) : (
            <div className="xposts-section__list">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="xposts-section__item"
                  {...activate(() => setExpandedId(expandedId === post.id ? null : post.id))}
                >
                  <div className="xposts-section__item-row">
                    <div className="xposts-section__item-text">
                      {expandedId === post.id
                        ? post.tweetText
                        : post.tweetText.length > 100
                          ? post.tweetText.slice(0, 100) + "..."
                          : post.tweetText}
                    </div>
                    <div className="xposts-section__item-meta">
                      <span className={`xposts-section__status ${statusClass(post.status)}`}>
                        {post.status}
                      </span>
                      <span className="xposts-section__date">
                        {formatDate(post.postedAt || post.createdAt)}
                      </span>
                    </div>
                  </div>
                  {post.status === "Failed" && post.errorMessage && (
                    <div className="xposts-section__error">{post.errorMessage}</div>
                  )}
                  {expandedId === post.id && post.tweetUrl && (
                    <a
                      className="xposts-section__view-link"
                      href={post.tweetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View on X
                    </a>
                  )}
                  {post.mediaThumbnailUrl && (
                    <img
                      src={post.mediaThumbnailUrl}
                      alt="Media"
                      className="xposts-section__thumb"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
